import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Pause, Play, ShieldAlert } from "lucide-react";
import { api } from "../lib/api";

const ROTATE_MS = 5000;

export default function ConvictedCarousel() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    // Pull a generous slice and filter to those with photos client-side.
    // We restrict to fully-adjudicated statuses to stay aligned with the
    // editorial rule the backend already enforces on photo uploads.
    api
      .get("/abusers", { params: { limit: 200, status: "Convicted" } })
      .then((r) => {
        const rows = (r.data?.items || []).filter(
          (a) => a.photo_url && a.photo_url.startsWith("https://")
        );
        setItems(rows);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (paused || items.length < 2) return;
    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % items.length);
    }, ROTATE_MS);
    return () => clearInterval(timerRef.current);
  }, [paused, items.length]);

  if (items.length === 0) return null;

  const cur = items[idx];
  const prev = () => setIdx((i) => (i - 1 + items.length) % items.length);
  const next = () => setIdx((i) => (i + 1) % items.length);

  return (
    <section
      className="border-y border-zinc-900 bg-zinc-900 text-white"
      data-testid="convicted-carousel"
      aria-label="Convicted animal abusers — rotating spotlight"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <div className="label-eyebrow text-red-500">Convicted — public record</div>
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter mt-1">
              Faces of the registry
            </h2>
          </div>
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-zinc-400">
            <span data-testid="carousel-counter">
              {idx + 1} / {items.length}
            </span>
            <button
              type="button"
              onClick={() => setPaused((p) => !p)}
              className="inline-flex items-center gap-1 px-2 py-1 border border-zinc-600 hover:border-white"
              aria-label={paused ? "Resume rotation" : "Pause rotation"}
              data-testid="carousel-pause"
            >
              {paused ? <Play size={11} /> : <Pause size={11} />}
              {paused ? "Play" : "Pause"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-stretch">
          {/* Photo */}
          <div className="md:col-span-5">
            <div className="relative aspect-[4/5] bg-zinc-800 border border-zinc-700 overflow-hidden">
              <img
                key={cur.id}
                src={cur.photo_url}
                alt={`${cur.name} — public record photo`}
                className="absolute inset-0 w-full h-full object-cover grayscale-[20%] transition-opacity duration-500"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
                data-testid="carousel-photo"
              />
              {cur.photo_source && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-3 py-1.5 text-[10px] uppercase tracking-widest font-mono text-zinc-300">
                  Photo: {cur.photo_source}
                </div>
              )}
            </div>
          </div>

          {/* Meta */}
          <div className="md:col-span-7 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest bg-red-600 text-white">
                  <ShieldAlert size={10} /> Convicted
                </span>
                {cur.case_id && (
                  <span className="label-eyebrow text-zinc-500">{cur.case_id}</span>
                )}
              </div>
              <h3
                className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-tight"
                data-testid="carousel-name"
              >
                {cur.name}
              </h3>
              <div className="mt-2 text-sm uppercase tracking-widest font-mono text-zinc-400">
                {cur.crime_type} · {cur.city}, {cur.state}
              </div>
              <p className="mt-4 text-base leading-relaxed text-zinc-200 line-clamp-4">
                {cur.crime_summary}
              </p>
              {cur.sentence && (
                <p className="mt-3 text-sm leading-relaxed">
                  <span className="label-eyebrow text-red-500 mr-2">Sentence</span>
                  <span className="font-semibold">{cur.sentence}</span>
                </p>
              )}
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link
                to={`/abusers/${cur.id}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white text-zinc-900 font-bold uppercase tracking-widest text-[11px] hover:bg-red-600 hover:text-white transition-colors"
                data-testid="carousel-cta"
              >
                View full case →
              </Link>
              <div className="flex items-center gap-2 ml-auto">
                <button
                  type="button"
                  onClick={prev}
                  className="inline-flex items-center justify-center w-9 h-9 border border-zinc-600 hover:border-white"
                  aria-label="Previous record"
                  data-testid="carousel-prev"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  onClick={next}
                  className="inline-flex items-center justify-center w-9 h-9 border border-zinc-600 hover:border-white"
                  aria-label="Next record"
                  data-testid="carousel-next"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Dots */}
        {items.length > 1 && (
          <div className="flex flex-wrap items-center gap-1.5 mt-6">
            {items.map((it, i) => (
              <button
                key={it.id}
                type="button"
                onClick={() => setIdx(i)}
                aria-label={`Go to ${it.name}`}
                className={`h-1.5 transition-all ${
                  i === idx ? "w-8 bg-red-600" : "w-4 bg-zinc-600 hover:bg-zinc-400"
                }`}
                data-testid={`carousel-dot-${i}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
